import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import LazyImage from '../gui/lazyImage';

/**
 * Public lottery prizes section on the convention page (convention.prizes).
 *
 * Shows every prize with its image and title. Once a winner is drawn (by an
 * official in the prizesAdmin drawer) the winner's name appears on the card so
 * shooters can see what they won and claim the prize in the kiosk. Managed by
 * /api/conventions/:id/prizes — winners are drawn server-side with a CSPRNG.
 */
@observer
class ConventionPrizes extends Component {
    render() {
        const { conventionStore } = this.props.stores;
        // conventionStore.participants holds the full user records get-convention
        // loads alongside the convention — the sportsClub fallback for winners
        // drawn before the club was stored denormalized on the prize.
        const { convention, participants: userRecords = [] } = conventionStore;

        if (!convention || !convention.id) {
            return null;
        }
        const prizes = Array.isArray(convention.prizes) ? convention.prizes : [];
        if (prizes.length === 0) {
            return null;
        }
        const drawnCount = prizes.filter(p => p && p.winner && p.winner.id > 0).length;

        return (
            <div class='px-3 mt-4 mb-2'>
                <div class='mb-1' style='font-size: 1rem; font-weight: 500;'>
                    <i class='fa-duotone fa-gift me-2' />
                    <Text id='convention.prizes-title'>Prizes</Text>
                    <span class='text-body-secondary ms-2' style='font-size: 0.8rem;'>({prizes.length})</span>
                </div>
                <div class='text-body-secondary mb-2' style='font-size: 0.85rem;'>
                    {drawnCount > 0 ? <>
                        <i class='fa-duotone fa-trophy me-1' />
                        <Text id='convention.prizes-info-drawn'>Winners are drawn among all participants — collect your prize in the kiosk!</Text>
                    </> : <>
                        <i class='fa-duotone fa-ticket me-1' />
                        <Text id='convention.prizes-info'>Winners are drawn among all participants of this convention.</Text>
                    </>}
                </div>
                <div class='d-flex flex-wrap gap-2 mb-2'>
                    {prizes.map(prize => {
                        const winner = prize.winner && prize.winner.id > 0 ? prize.winner : null;
                        const winnerName = winner ? `${winner.firstname || ''} ${winner.lastname || ''}`.trim() : '';
                        const winnerClub = winner
                            ? winner.sportsClub || (userRecords.find(u => u && u.id === winner.id) || {}).sportsClub
                            : null;
                        const image = prize.image || {};
                        return (
                            <div key={prize.md5} class='card' style='width: 170px;'>
                                {image.s3SmallLink || image.s3MediumLink || image.s3Link ? (
                                    <LazyImage
                                        className='card-img-top overflow-hidden'
                                        src={image.s3SmallLink || image.s3MediumLink || image.s3Link}
                                        lowResBase64={image.lowResBase64}
                                        alt={prize.title || ''}
                                        loading='lazy'
                                        style='height: 110px; object-fit: cover;'
                                    />
                                ) : (
                                    <div class='card-img-top d-flex align-items-center justify-content-center bg-body-secondary' style='height: 110px;'>
                                        <i class='fa-duotone fa-gift text-body-secondary' style='font-size: 2.5rem;' />
                                    </div>
                                )}
                                <div class='card-body p-2'>
                                    <div class='fw-bold' style='font-size: 0.85rem; line-height: 1.15rem;'>
                                        {prize.title}
                                    </div>
                                    {winner ? <>
                                        <div class='mt-1' style='font-size: 0.8rem;'>
                                            <i class='fa-duotone fa-trophy text-warning me-1' />
                                            {winnerName}
                                        </div>
                                        {winnerClub && (
                                            <div class='text-body-secondary text-truncate' style='font-size: 0.7rem;'>
                                                {winnerClub}
                                            </div>
                                        )}
                                        {prize.claimed ? (
                                            <span class='badge bg-success mt-1' style='font-size: 0.65rem;'>
                                                <i class='fa-solid fa-check me-1' />
                                                <Text id='convention.prize-claimed'>Picked up</Text>
                                            </span>
                                        ) : (
                                            <span class='badge bg-warning text-dark mt-1' style='font-size: 0.65rem;'>
                                                <i class='fa-duotone fa-store me-1' />
                                                <Text id='convention.prize-claim-kiosk'>Collect in the kiosk</Text>
                                            </span>
                                        )}
                                    </> : (
                                        <div class='text-body-secondary mt-1' style='font-size: 0.75rem;'>
                                            <i class='fa-duotone fa-dice me-1' />
                                            <Text id='convention.prize-not-drawn'>Not drawn yet</Text>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }
}

export default ConventionPrizes;
